// =============================================================================
// Household setup
//
// ⚠️ RETIRED (v0.2.25): `setupHouseholdForCole` below is NO LONGER CALLED.
// It was the original one-time bootstrap that seeded the Mullinax family
// (Cole's household, Mia + Harper, Mia's profile, target schools, 12 seeded
// scholarships, 25 AI-discovery inbox items). The Mullinax household now
// exists in production, so this seeder is never needed again.
//
// New signups without an invite code create their OWN empty household via
// `createHousehold()` in ./createHousehold.ts — do NOT wire this function
// back into the signup flow, or new users would get a clone of the Mullinax
// family's data. It is kept here only as a record of the original seed.
//
// `createInviteCode` / `generateInviteCode` at the bottom of this file ARE
// still in use (by the Household page) and are not affected.
// =============================================================================
//
// ORIGINAL DESCRIPTION (for reference):
// setupHouseholdForCole — runs once after Cole signs up to create:
//   - The Mullinax household document
//   - Cole's parent member doc
//   - Mia's applicant record + profile + target schools
//   - Harper's placeholder applicant record (for future)
//   - 12 seeded scholarship records under Mia
//   - 25 inbox items from the AI discovery run
//   - Default settings (queue_weights, notification_prefs, discovery_prefs)
//
// Idempotent: checks for an existing household first and skips if found.
//
// DIAGNOSTIC LOGGING (v0.2.6): every step logs its progress and any failures
// to the browser console with prefix [setup]. After running, copy the entire
// console output to share with the developer. Logs stay in for future debugging.
// =============================================================================

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
  limit,
  Timestamp,
  FirestoreError,
} from 'firebase/firestore';
import { db } from '../firebase';
import { SEEDED_SCHOLARSHIPS } from '../seeds/scholarships';
import { MIA_PROFILE_SEED, MIA_TARGET_SCHOOLS } from '../seeds/profile';
import { inboxItemFingerprint } from '@scholarship-app/shared';
import type { User } from 'firebase/auth';
import initialDiscovery from '../seeds/initial-ai-discovery.json';

function genId(prefix: string): string {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}`;
}

// ---- Diagnostic logger ----
const log = (msg: string, ...args: unknown[]) => {
  console.log(`%c[setup] ${msg}`, 'color:#4f46e5;font-weight:600', ...args);
};
const logOk = (msg: string, ...args: unknown[]) => {
  console.log(`%c[setup] ✓ ${msg}`, 'color:#059669;font-weight:600', ...args);
};
const logErr = (step: string, err: unknown) => {
  const e = err as FirestoreError & { stack?: string };
  console.log(
    `%c[setup] ✗ FAILED at: ${step}`,
    'color:#dc2626;font-weight:700;background:#fef2f2;padding:2px 6px'
  );
  console.log('%c  error code:    ', 'color:#dc2626', e?.code ?? '(none)');
  console.log('%c  error message: ', 'color:#dc2626', e?.message ?? String(err));
  console.log('%c  full error:    ', 'color:#dc2626', err);
  if (e?.stack) console.log('%c  stack:         ', 'color:#dc2626', e.stack);
};

export interface SetupResult {
  householdId: string;
  miaApplicantId: string;
  harperApplicantId: string;
  scholarshipsCreated: number;
  inboxItemsCreated: number;
}

export async function setupHouseholdForCole(user: User): Promise<SetupResult> {
  if (!db) throw new Error('Firestore not configured');

  log('=== setupHouseholdForCole START ===');
  log('User:', { uid: user.uid, email: user.email, displayName: user.displayName });

  // Check if Cole already has a household.
  log('Step 0: Checking for existing household via member_uids query...');
  let existing;
  try {
    existing = await getDocs(
      query(
        collection(db, 'households'),
        where('member_uids', 'array-contains', user.uid),
        limit(1)
      )
    );
    logOk(`Step 0 query succeeded; ${existing.size} existing households found.`);
  } catch (e) {
    logErr('Step 0 — initial household query', e);
    throw e;
  }
  if (!existing.empty) {
    const hh = existing.docs[0];
    // Already set up; return existing IDs.
    log('Existing household found; skipping seeding.', hh.id);
    const applicantsSnap = await getDocs(collection(db, 'households', hh.id, 'applicants'));
    const mia = applicantsSnap.docs.find((d) => d.data().display_name?.includes('Mia'));
    const harper = applicantsSnap.docs.find((d) => d.data().display_name?.includes('Harper'));
    return {
      householdId: hh.id,
      miaApplicantId: mia?.id ?? '',
      harperApplicantId: harper?.id ?? '',
      scholarshipsCreated: 0,
      inboxItemsCreated: 0,
    };
  }

  // === Create new household ===
  // CRITICAL: writes must be split into sequential batches because Firestore
  // security rules evaluate each write in a batch against the pre-batch
  // committed state, not against earlier writes in the same batch. The
  // applicant/profile/settings rules check `isParent(householdId)` which
  // reads from `members/{uid}`. If the member doc is in the same batch, the
  // check fails. Solution: commit household + member doc first, then commit
  // everything else after the member doc is visible.
  const householdId = genId('hh');
  const miaApplicantId = genId('app');
  const harperApplicantId = genId('app');

  const householdRef = doc(db, 'households', householdId);
  const memberRef = doc(db, 'households', householdId, 'members', user.uid);
  const miaApplicantRef = doc(db, 'households', householdId, 'applicants', miaApplicantId);
  const harperApplicantRef = doc(db, 'households', householdId, 'applicants', harperApplicantId);
  const miaProfileRef = doc(db, 'households', householdId, 'applicants', miaApplicantId, 'profile', 'profile');

  const now = serverTimestamp();

  // === Batch 1a: household doc + Cole's parent member doc ===
  // This establishes Cole as a parent of the household. Once this commits,
  // isParent() will return true and subsequent writes will pass the rules.
  const batch1a = writeBatch(db);

  batch1a.set(householdRef, {
    household_id: householdId,
    name: 'Mullinax Family',
    created_by: user.uid,
    member_uids: [user.uid],
    created_at: now,
    updated_at: now,
    archived: false,
    settings_initialized: true,
  });

  // Derive a sensible default display name from email if no displayName set
  // on the auth user. Email/password signups don't auto-populate displayName.
  const fallbackName = (user.email ?? '').split('@')[0] || 'Member';
  const displayNameToUse = user.displayName || fallbackName;

  batch1a.set(memberRef, {
    user_id: user.uid,
    email: user.email ?? '',
    display_name: displayNameToUse,
    role: 'parent',
    applicant_id: null,
    invited_by: null,
    invited_at: now,
    accepted_at: now,
    status: 'active',
    last_active_at: now,
    archived: false,
    email_preferences: { deadline_reminders: true },
  });

  log('Batch 1a: about to commit household doc + parent member doc...');
  log('  household_id:', householdId);
  log('  member_uids will be: [' + user.uid + ']');
  log('  created_by:', user.uid);
  try {
    await batch1a.commit();
    logOk('Batch 1a committed successfully.');
  } catch (e) {
    logErr('Batch 1a — household + member create', e);
    throw e;
  }

  // === Batch 1b: applicants + profile + target schools + settings ===
  // Now that Cole's member doc is committed, isParent() returns true and
  // these writes will pass the rules.
  const batch1b = writeBatch(db);

  batch1b.set(miaApplicantRef, {
    applicant_id: miaApplicantId,
    linked_user_id: null,
    display_name: 'Mia Mullinax',
    created_at: now,
    updated_at: now,
    archived: false,
  });

  batch1b.set(harperApplicantRef, {
    applicant_id: harperApplicantId,
    linked_user_id: null,
    display_name: 'Harper Mullinax',
    created_at: now,
    updated_at: now,
    archived: false,
  });

  batch1b.set(miaProfileRef, {
    ...MIA_PROFILE_SEED,
    created_at: now,
    updated_at: now,
    completeness_score: 0.85,
  });

  for (const school of MIA_TARGET_SCHOOLS) {
    const schoolRef = doc(
      db,
      'households',
      householdId,
      'applicants',
      miaApplicantId,
      'target_schools',
      school.school_id
    );
    batch1b.set(schoolRef, {
      ...school,
      created_at: now,
      updated_at: now,
      archived: false,
    });
  }

  // Settings
  batch1b.set(doc(db, 'households', householdId, 'settings', 'queue_weights'), {
    w_deadline: 0.30,
    w_amount: 0.30,
    w_fit: 0.25,
    w_effort: 0.15,
    primary_tier_boost_points: 5,
    backup_tier_boost_points: 2,
    updated_at: now,
    updated_by: user.uid,
  });
  batch1b.set(doc(db, 'households', householdId, 'settings', 'notification_prefs'), {
    email_reminders_enabled: true,
    reminder_days_before: [30, 14, 7, 3, 1],
    morning_digest_enabled: false,
    digest_send_time_local: '06:00',
    timezone: 'America/New_York',
    updated_at: now,
  });
  batch1b.set(doc(db, 'households', householdId, 'settings', 'discovery_prefs'), {
    daily_check_enabled: true,
    daily_check_time_local: '05:00',
    timezone: 'America/New_York',
    max_inbox_items_per_day: 25,
    email_digest_of_new_items: false,
    ai_discovery_reminder_cadence_days: 30,
    discovery_sprint_months: [9, 1, 9],
    updated_at: now,
  });

  log('Batch 1b: about to commit applicants + profile + target_schools + settings...');
  log('  applicant IDs:', { mia: miaApplicantId, harper: harperApplicantId });
  log('  target schools:', MIA_TARGET_SCHOOLS.map((s) => s.school_id));
  try {
    await batch1b.commit();
    logOk('Batch 1b committed successfully.');
  } catch (e) {
    logErr('Batch 1b — applicants/profile/schools/settings', e);
    throw e;
  }

  // === Batch 2: seeded scholarships ===
  const scholarshipsBase = collection(db, 'households', householdId, 'applicants', miaApplicantId, 'scholarships');
  const batch2 = writeBatch(db);
  let scholarshipsCreated = 0;
  for (const seed of SEEDED_SCHOLARSHIPS) {
    const sid = genId('sch');
    const sref = doc(scholarshipsBase, sid);
    batch2.set(sref, {
      ...seed,
      scholarship_id: sid,
      created_at: now,
      updated_at: now,
      status_changed_at: now,
      discovered_at: now,
      created_by: user.uid,
      updated_by: user.uid,
    });
    scholarshipsCreated++;
  }
  log('Batch 2: about to commit', scholarshipsCreated, 'seeded scholarships...');
  try {
    await batch2.commit();
    logOk('Batch 2 committed successfully.');
  } catch (e) {
    logErr('Batch 2 — seeded scholarships', e);
    throw e;
  }

  // === Batch 3: inbox items from AI discovery run ===
  const inboxBase = collection(db, 'households', householdId, 'inbox');
  const batch3 = writeBatch(db);
  let inboxItemsCreated = 0;
  const runId = `run_${initialDiscovery.ai_discovery_run.run_date.replace(/-/g, '_')}`;
  for (const item of initialDiscovery.ai_discovery_run.items) {
    const itemId = genId('inbox');
    const iref = doc(inboxBase, itemId);
    const fingerprint = inboxItemFingerprint(item.name, item.sponsor);
    batch3.set(iref, {
      item_id: itemId,
      applicant_id: miaApplicantId,
      source: 'ai_discovery_import',
      discovery_run_id: runId,
      name: item.name,
      sponsor: item.sponsor,
      url: item.url,
      url_verified: item.url_verified,
      amount_min: item.amount_min,
      amount_max: item.amount_max,
      amount_notes: item.amount_notes,
      deadline_approx: item.deadline_approx,
      deadline_rolling: item.deadline_rolling,
      match_strength: item.match_strength,
      why_match: item.why_match,
      competitiveness: item.competitiveness,
      effort: item.effort,
      tags: item.tags,
      requirements_summary: item.requirements_summary,
      need_based: item.need_based,
      homeschool_friendly: item.homeschool_friendly,
      fingerprint,
      status: 'new',
      snooze_until: null,
      promoted_to_scholarship_id: null,
      discovered_at: now,
      reviewed_at: null,
      reviewed_by: null,
      notes: item.notes ?? null,
    });
    inboxItemsCreated++;
  }
  log('Batch 3: about to commit', inboxItemsCreated, 'inbox items...');
  try {
    await batch3.commit();
    logOk('Batch 3 committed successfully.');
  } catch (e) {
    logErr('Batch 3 — inbox items', e);
    throw e;
  }

  logOk('=== setupHouseholdForCole COMPLETE ===');
  log('Summary:', {
    householdId,
    miaApplicantId,
    harperApplicantId,
    scholarshipsCreated,
    inboxItemsCreated,
  });

  return {
    householdId,
    miaApplicantId,
    harperApplicantId,
    scholarshipsCreated,
    inboxItemsCreated,
  };
}

// Helper: produce a 6-char invite code for adding Karen/Mia after Cole's setup.
export function generateInviteCode(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // no easily-confused chars
  let s = '';
  for (let i = 0; i < 6; i++) s += chars[Math.floor(Math.random() * chars.length)];
  return s;
}

export async function createInviteCode(opts: {
  householdId: string;
  role: 'parent' | 'applicant';
  applicantId: string | null;
  createdBy: string;
}): Promise<string> {
  if (!db) throw new Error('Firestore not configured');
  const code = generateInviteCode();
  await setDoc(doc(db, 'invite_codes', code), {
    code,
    // v0.2.27: member codes now carry an explicit type. Redemption and the
    // rules treat a missing type as a member code, so older codes still work.
    type: opts.role, // 'parent' | 'applicant'
    household_id: opts.householdId,
    role: opts.role,
    applicant_id: opts.applicantId,
    created_by: opts.createdBy,
    created_at: serverTimestamp(),
    expires_at: Timestamp.fromMillis(Date.now() + 7 * 24 * 60 * 60 * 1000),
    used: false,
    used_by: null,
  });
  return code;
}

// =============================================================================
// New-household invite codes (v0.2.27)
//
// A `new_household` code authorizes creating a brand-new household. Unlike
// member codes it has NO household_id (the household doesn't exist yet). It
// can only be minted by a parent of a household flagged
// can_issue_household_codes (the Mullinax household). The issuing household id
// is recorded as `issuer_household_id` so the security rules can verify the
// minter's authority and so issued codes can be managed/deleted.
// =============================================================================

export async function createNewHouseholdCode(opts: {
  /** The issuing household (must have can_issue_household_codes == true). */
  issuerHouseholdId: string;
  createdBy: string;
}): Promise<string> {
  if (!db) throw new Error('Firestore not configured');
  const code = generateInviteCode();
  await setDoc(doc(db, 'invite_codes', code), {
    code,
    type: 'new_household',
    issuer_household_id: opts.issuerHouseholdId,
    created_by: opts.createdBy,
    created_at: serverTimestamp(),
    expires_at: Timestamp.fromMillis(Date.now() + 7 * 24 * 60 * 60 * 1000),
    used: false,
    used_by: null,
  });
  return code;
}

// =============================================================================
// One-time backfill (v0.2.27): authorize the Mullinax household to issue
// new-household codes.
//
// The can_issue_household_codes flag must be set on exactly one household —
// the original Mullinax household — so only Cole can mint new-household codes.
// New households created via createHousehold() never get the flag.
//
// The Mullinax household is identified unambiguously as the household whose
// creator's account email is the known owner email. This backfill runs when
// that user opens the Household page; it sets the flag once and is a no-op
// thereafter. It cannot misfire for any other household — only a session
// signed in as that exact email, viewing their own household, triggers it.
// =============================================================================

const HOUSEHOLD_CODE_ISSUER_EMAIL = 'colemullinax@yahoo.com';

/**
 * If the current user is the designated owner and their household lacks the
 * can_issue_household_codes flag, set it. Returns true if it set the flag.
 * Safe to call on every Household-page load — a no-op after the first.
 */
export async function ensureHouseholdCodeIssuer(opts: {
  householdId: string;
  userEmail: string | null | undefined;
}): Promise<boolean> {
  if (!db) return false;
  // Only the designated owner email can trigger this.
  if (
    !opts.userEmail ||
    opts.userEmail.toLowerCase() !== HOUSEHOLD_CODE_ISSUER_EMAIL
  ) {
    return false;
  }
  const householdRef = doc(db, 'households', opts.householdId);
  const snap = await getDoc(householdRef);
  if (!snap.exists()) return false;
  if (snap.data().can_issue_household_codes === true) {
    return false; // Already set.
  }
  await updateDoc(householdRef, { can_issue_household_codes: true });
  return true;
}
